// @flow

import { type Country } from '@tectonics/prs/country';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type OutgoingPaymentMapper } from './types';

import { captchaOutgoingPaymentMapper } from '../../../captcha';
import { metadataOutgoingPaymentMapper } from '../../metadata';
import { fingerprintOutgoingPaymentMapper } from '../../fingerprint';
import { customerOutgoingPaymentMapper } from '../../customer';
import { threeDSecureOutgoingPaymentMapper } from '../../threedsecure';
import { incomingPaymentMapper } from '../../incoming-payment';
import { instalmentsOutgoingPaymentMapper } from '../../receipts/receipt/instalments-plan';

import { argentineOutgoingPaymentMapper } from '../../argentina';
import { bolivianOutgoingPaymentMapper } from '../../bolivia';
import { brazilianOutgoingPaymentMapper } from '../../brazil';
import { chileanOutgoingPaymentMapper } from '../../chile';
import { colombianOutgoingPaymentMapper } from '../../colombia';
import { costaRicanOutgoingPaymentMapper } from '../../costa-rica/outgoing-payment-mapper';
import { dominicanOutgoingPaymentMapper } from '../../dominican-republic/outgoing-payment-mapper';
import { ecuadorianOutgoingPaymentMapper } from '../../ecuador';
import { elSalvadorOutgoingPaymentMapper } from '../../el-salvador/outgoing-payment-mapper';
import { guatemalanOutgoingPaymentMapper } from '../../guatemala/outgoing-payment-mapper';
import { kenyanOutgoingPaymentMapper } from '../../kenya/outgoing-payment-mapper';
import { mexicanOutgoingPaymentMapper } from '../../mexico';
import { nigerianOutgoingPaymentMapper } from '../../nigeria/outgoing-payment-mapper';
import { panamanianOutgoingPaymentMapper } from '../../panama/outgoing-payment-mapper';
import { paraguayanOutgoingPaymentMapper } from '../../paraguay/outgoing-payment-mapper';
import { peruvianOutgoingPaymentMapper } from '../../peru';
import { southAfricanOutgoingPaymentMapper } from '@tectonics/prs/south-africa';
import { tanzanianOutgoingPaymentMapper } from '../../tanzania/outgoing-payment-mapper';
import { ugandaOutgoingPaymentMapper } from '../../uganda/outgoing-payment-mapper';
import { zambiaOutgoingPaymentMapper } from '../../zambia/outgoing-payment-mapper';
import { uruguayanOutgoingPaymentMapper } from '../../uruguay';
import { indiaOutgoingPaymentMapper } from '../../india/outgoing-payment-mapper';
import { egyptOutgoingPaymentMapper } from '../../egypt/outgoing-payment-mapper';
import { ghanaOutgoingPaymentMapper } from '../../ghana/outgoing-payment-mapper';

import { tefOutgoingPaymentMapper } from '../../tef';
import { oxxoOutgoingPaymentMapper } from '../../oxxo';
import { oxxopayOutgoingPaymentMapper } from '../../oxxopay';
import { speiOutgoingPaymentMapper } from '../../spei';
import { pixOutgoingPaymentMapper } from '../../pix';
import { boletoOutgoingPaymentMapper } from '../../boleto';
import { googlePayOutgoingPaymentMapper } from '../../google-pay';
import { creditCardOutgoingPaymentMapper } from '../../credit-card';
import { voucherCardOutgoingPaymentMapper } from '../../vouchercard/outgoing-payment-mapper';
import { multibeneficiosOutgoingPaymentMapper } from '../../multibeneficios/outgoing-payment-mapper';
import { debitCardOutgoingPaymentMapper } from '../../debit-card';
import { bankTransferOutgoingPaymentMapper } from '../../banktransfer';
import { multicajaOutgoingPaymentMapper } from '../../multicaja';
import { servipagOutgoingPaymentMapper } from '../../servipag';
import { eftColombiaOutgoingPaymentMapper } from '../../eft';
import { sencillitoOutgoingPaymentMapper } from '../../sencillito';
import { webpayOutgoingPaymentMapper } from '../../webpay';
import { pagoefectivoOutgoingPaymentMapper } from '../../pagoefectivo';
import { safetypayOutgoingPaymentMapper } from '../../safetypay';
import { safetypayCashOutgoingPaymentMapper } from '../../safetypay-cash';
import { safetypayOnlineOutgoingPaymentMapper } from '../../safetypay-online';
import { ewalletOutgoingPaymentMapper } from '../../ewallet';
import { efectyOutgoingPaymentMapper } from '../../efecty';
import { cashMexicoOutgoingPaymentMapper } from '../../cash-mexico';
import { cashBoliviaOutgoingPaymentMapper } from '../../cash-bolivia';
import { argentineCashOutgoingPaymentMapper } from '../../cash-argentina';
import { cashGuatemalanOutgoingPaymentMapper } from '../../cash-guatemala';
import { cashCostaRicanOutgoingPaymentMapper } from '../../cash-costa-rica';
import { cashPanamanianOutgoingPaymentMapper } from '../../cash-panamanian';
import { cashElSalvadorianOutgoingPaymentMapper } from '../../cash-el-salvador';
import { externalColombiaOutgoingPaymentMapper } from '../../external-colombia';
import { externalSouthAfricanOutgoingPaymentMapper } from '../../external-southafrica';
import { debinOutgoingPaymentMapper } from '../../debin';
import { pixPispOutgoingPaymentMapper } from '../../pix-pisp';
import { nupayOutgoingPaymentMapper } from '../../nupay';
import { mpcreditoOutgoingPaymentMapper } from '../../mpcredito';
import { externalChileOutgoingPaymentMapper } from '../../external-chile';
import {nibbsOutgoingPaymentMapper} from '@tectonics/prs/nibbs';
import { jamaicaOutgoingPaymentMapper } from '@tectonics/prs/jamaica/outgoing-payment-mapper';
import { externalEgyptOutgoingPaymentMapper } from '../../external-egypt/external-egypt-outgoing-payment-mapper';

import { externalMexicoOutgoingPaymentMapper } from '../../external-mexico/external-mexico-outgoing-payment-mapper';
import { externalNigeriaOutgoingPaymentMapper } from '../../external-nigeria';

export function createOutgoingPaymentMappers(userInputValues: UserInputValues, country: Country): ImmutableArray<OutgoingPaymentMapper> {
  const selectedPaymentMethod = userInputValues['selectedPaymentMethod'];

  return [
    metadataOutgoingPaymentMapper,
    fingerprintOutgoingPaymentMapper,
    captchaOutgoingPaymentMapper,
    customerOutgoingPaymentMapper,
    getCountryMapper(country),
    getPaymentMethodMapper(selectedPaymentMethod, country),
    instalmentsOutgoingPaymentMapper,
    threeDSecureOutgoingPaymentMapper,
    incomingPaymentMapper,
  ];
}

function getPaymentMethodMapper(selectedPaymentMethod: string, country: Country): OutgoingPaymentMapper {
  switch (selectedPaymentMethod) {
    case 'creditcard':
      return creditCardOutgoingPaymentMapper;
    case 'debitcard':
      return debitCardOutgoingPaymentMapper;
    case 'boleto':
      return boletoOutgoingPaymentMapper;
    case 'banktransfer':
      return banktransferProviderOutgoingMapperResolver(country);
    case 'tef':
      return tefOutgoingPaymentMapper;
    case 'oxxo':
      return oxxoOutgoingPaymentMapper;
    case 'oxxopay':
      return oxxopayOutgoingPaymentMapper;
    case 'spei':
      return speiOutgoingPaymentMapper;
    case 'cash':
      return cashOutgoingMapperResolver(country);
    case 'google':
      return googlePayOutgoingPaymentMapper;
    case 'multicaja':
      return multicajaOutgoingPaymentMapper;
    case 'servipag':
      return servipagOutgoingPaymentMapper;
    case 'eft':
      return eftColombiaOutgoingPaymentMapper;
    case 'sencillito':
      return sencillitoOutgoingPaymentMapper;
    case 'webpay':
      return webpayOutgoingPaymentMapper;
    case 'pagoefectivo':
      return pagoefectivoOutgoingPaymentMapper;
    case 'safetypay':
      return safetypayOutgoingPaymentMapper;
    case 'safetypay-cash':
      return safetypayCashOutgoingPaymentMapper;
    case 'safetypay-online':
      return safetypayOnlineOutgoingPaymentMapper;
    case 'ewallet':
      return ewalletOutgoingPaymentMapper;
    case 'pix':
      return pixOutgoingPaymentMapper;
    case 'pix-pisp':
      return pixPispOutgoingPaymentMapper;
    case 'vouchercard':
      return voucherCardOutgoingPaymentMapper;
    case 'multibeneficios':
      return multibeneficiosOutgoingPaymentMapper;
    case 'externalpayprovider':
      return externalPayProviderOutgoingMapperResolver(country);

    case 'nupay':
      return nupayOutgoingPaymentMapper;
    case 'mpcredito':
      return mpcreditoOutgoingPaymentMapper;
    case 'nibbs':
      return nibbsOutgoingPaymentMapper;
    default:
      throw new Error(`outgoing payment mapper: ${selectedPaymentMethod} not mapped`);
  }
}

function cashOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'mx':
      return cashMexicoOutgoingPaymentMapper;
    case 'bo':
      return cashBoliviaOutgoingPaymentMapper;
    case 'ar':
      return argentineCashOutgoingPaymentMapper;
    case 'co':
      return efectyOutgoingPaymentMapper;
    case 'gt':
      return cashGuatemalanOutgoingPaymentMapper;
    case 'cr':
      return cashCostaRicanOutgoingPaymentMapper;
    case 'pa':
      return cashPanamanianOutgoingPaymentMapper;
    case 'sv':
      return cashElSalvadorianOutgoingPaymentMapper;
    default:
      throw new Error(`outgoing payment mapper for method cash in country: ${country} not found`);

  }
}


function externalPayProviderOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'eg':
      return externalEgyptOutgoingPaymentMapper;
    case 'za':
      return externalSouthAfricanOutgoingPaymentMapper;
    case 'co':
      return externalColombiaOutgoingPaymentMapper;
    case 'cl':
      return externalChileOutgoingPaymentMapper;
    case 'mx':
      return externalMexicoOutgoingPaymentMapper;
    case 'ng':
      return externalNigeriaOutgoingPaymentMapper;
    default:
      throw new Error(`Outgoing payment mapper for method externalpayprovider in country: ${country} not found`);
  }
}

function banktransferProviderOutgoingMapperResolver(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'ar':
      return debinOutgoingPaymentMapper;
    default:
      return bankTransferOutgoingPaymentMapper;
  }
}

function getCountryMapper(country: Country): OutgoingPaymentMapper {
  switch (country) {
    case 'br':
      return brazilianOutgoingPaymentMapper;
    case 'ar':
      return argentineOutgoingPaymentMapper;
    case 'bo':
      return bolivianOutgoingPaymentMapper;
    case 'cl':
      return chileanOutgoingPaymentMapper;
    case 'co':
      return colombianOutgoingPaymentMapper;
    case 'ec':
      return ecuadorianOutgoingPaymentMapper;
    case 'mx':
      return mexicanOutgoingPaymentMapper;
    case 'pe':
      return peruvianOutgoingPaymentMapper;
    case 'uy':
      return uruguayanOutgoingPaymentMapper;
    case 'pa':
      return panamanianOutgoingPaymentMapper;
    case 'sv':
      return elSalvadorOutgoingPaymentMapper;
    case 'gt':
      return guatemalanOutgoingPaymentMapper;
    case 'do':
      return dominicanOutgoingPaymentMapper;
    case 'cr':
      return costaRicanOutgoingPaymentMapper;
    case 'py':
      return paraguayanOutgoingPaymentMapper;
    case 'za':
      return southAfricanOutgoingPaymentMapper;
    case 'ke':
      return kenyanOutgoingPaymentMapper;
    case 'ng':
      return nigerianOutgoingPaymentMapper;
    case 'in':
      return indiaOutgoingPaymentMapper;
    case 'tz':
      return tanzanianOutgoingPaymentMapper;
    case 'ug':
      return ugandaOutgoingPaymentMapper;
    case 'zm':
      return zambiaOutgoingPaymentMapper;
    case 'eg':
      return egyptOutgoingPaymentMapper;
    case 'gh':
      return ghanaOutgoingPaymentMapper;
    case 'jm':
      return jamaicaOutgoingPaymentMapper;
    default:
      throw new Error(`No country mapper for country ${country}`);
  }
}
